<template>
  <div class="app-subscription-plan" v-loading="dataLoading">

    <!-- 套餐列表 -->
    <el-table :data="tableData" row-key="id">
      <el-table-column prop="id" label="ID" width="60" />
      <el-table-column prop="productId" label="Product ID" min-width="170" />
      <el-table-column prop="name" label="Name" min-width="100" />
      <el-table-column prop="targetLevel" label="Level" width="90" />
      <el-table-column label="Price (¥)" width="90" align="right">
        <template slot-scope="scope">
          {{ (scope.row.originalPriceCents / 100).toFixed(2) }}
        </template>
      </el-table-column>
      <el-table-column label="Promotion" min-width="90" align="center">
        <template slot-scope="scope">
          <div v-if="scope.row.originalPriceCents > 0" class="promotion-cell">
            <div class="status-dot" :class="scope.row.promotionPriceCents !== null && scope.row.promotionPriceCents !== undefined ? 'success' : 'danger'" />
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="durationUnit" label="Period" width="80" align="center" />
      <el-table-column label="Duration" width="135" align="center">
        <template slot-scope="scope">
          {{ scope.row.durationValue === 0 ? '∞' : scope.row.durationValue }}
        </template>
      </el-table-column>
      <el-table-column prop="dailyAnalysis" label="Daily Analysis" width="120" align="center" />
      <el-table-column prop="dailyImprove" label="Daily Improve" width="120" align="center" />
      <el-table-column prop="sortOrder" label="Sort" width="65" align="center" />
      <el-table-column label="Active" width="70" align="center">
        <template slot-scope="scope">
          <div class="status-dot" :class="scope.row.isActive ? 'success' : 'danger'" />
        </template>
      </el-table-column>
      <el-table-column label="Updated At" min-width="175">
        <template slot-scope="scope">
          <i class="el-icon-time" />
          <span style="margin-left: 8px">{{ scope.row.updatedAt | displayTime() }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Action" width="80" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" icon="el-icon-edit" size="small" @click="openDialog(scope.row)" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑对话框 -->
    <el-dialog title="Edit Subscription Plan" :visible.sync="dialogVisible" width="560px" @close="resetEditForm">
      <!-- 只读信息 -->
      <div class="plan-readonly-info">
        <span class="info-item"><b>Product ID:</b> {{ form.productId }}</span>
        <span class="info-item"><b>Name:</b> {{ form.name }}</span>
        <span class="info-item"><b>Level:</b> {{ form.targetLevel }}</span>
      </div>

      <el-form ref="form" :model="form" :rules="rules" label-width="140px" size="small">
        <el-form-item label="Billing Period" prop="durationUnit">
          <el-select v-model="form.durationUnit" style="width: 200px">
            <el-option label="Month" value="month" />
            <el-option label="Year" value="year" />
          </el-select>
        </el-form-item>

        <el-form-item label="Price (¥)" prop="priceYuan">
          <el-input-number v-model="form.priceYuan" :min="0" :precision="2" :step="1" style="width: 200px" />
        </el-form-item>

        <el-form-item label="Duration" prop="durationValue">
          <el-input-number v-model="form.durationValue" :min="0" :step="1" style="width: 200px" />
          <div class="field-hint">0 = unlimited</div>
        </el-form-item>

        <el-form-item label="Daily Analysis" prop="dailyAnalysis">
          <el-input-number v-model="form.dailyAnalysis" :min="0" :step="1" style="width: 200px" />
        </el-form-item>

        <el-form-item label="Daily Improve" prop="dailyImprove">
          <el-input-number v-model="form.dailyImprove" :min="0" :step="1" style="width: 200px" />
        </el-form-item>

        <el-divider content-position="left">Promotion Settings</el-divider>

        <el-form-item label="Enable Promotion">
          <el-switch v-model="form.promotionEnabled" />
        </el-form-item>

        <template v-if="form.promotionEnabled">
          <el-form-item label="Promo Price (¥)">
            <el-input-number v-model="form.promotionPriceYuan" :min="0" :precision="2" :step="1" style="width: 200px" />
          </el-form-item>

          <el-form-item label="Start At">
            <el-date-picker
              v-model="form.promotionStartAt"
              type="datetime"
              clearable
              style="width: 100%"
            />
            <div class="field-hint">Empty means no start time limit</div>
          </el-form-item>

          <el-form-item label="End At">
            <el-date-picker
              v-model="form.promotionEndAt"
              type="datetime"
              clearable
              style="width: 100%"
            />
            <div class="field-hint">Empty means no end time limit</div>
          </el-form-item>
        </template>
      </el-form>

      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitForm">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchAppSubscriptionPlans,
  upsertAppSubscriptionPlan,
} from "@/api/admin";

export default {
  name: "AppTabSubscriptionPlan",
  data() {
    return {
      tableData: [],
      dataLoading: false,
      saving: false,
      dialogVisible: false,
      form: this.defaultForm(),
      rules: {
        durationUnit: [
          { required: true, message: "Billing period is required", trigger: "change" },
        ],
        priceYuan: [
          { required: true, message: "Price is required", trigger: "blur" },
        ],
        durationValue: [
          { required: true, message: "Duration is required", trigger: "blur" },
        ],
        dailyAnalysis: [
          { required: true, message: "Daily analysis is required", trigger: "blur" },
        ],
        dailyImprove: [
          { required: true, message: "Daily improve is required", trigger: "blur" },
        ],
      },
    };
  },
  mounted() {
    this.active();
  },
  methods: {
    defaultForm() {
      return {
        id: 0,
        productId: "",
        name: "",
        targetLevel: "",
        durationUnit: "month",
        priceYuan: 0,
        durationValue: 1,
        dailyAnalysis: 0,
        dailyImprove: 0,
        sortOrder: 1,
        isActive: true,
        promotionEnabled: false,
        promotionPriceYuan: 0,
        promotionStartAt: null,
        promotionEndAt: null,
      };
    },
    active() {
      this.fetchData();
    },
    fetchData() {
      this.dataLoading = true;
      fetchAppSubscriptionPlans()
        .then((response) => {
          if (response.success) {
            this.tableData = response.result.items || [];
          }
        })
        .finally(() => {
          this.dataLoading = false;
        });
    },
    openDialog(row) {
      const hasPromotion = row.promotionPriceCents !== undefined && row.promotionPriceCents !== null;
      this.form = {
        id: row.id,
        productId: row.productId,
        name: row.name,
        targetLevel: row.targetLevel,
        durationUnit: row.durationUnit || "month",
        priceYuan: row.originalPriceCents !== undefined ? row.originalPriceCents / 100 : 0,
        durationValue: row.durationValue,
        dailyAnalysis: row.dailyAnalysis,
        dailyImprove: row.dailyImprove,
        sortOrder: row.sortOrder,
        isActive: row.isActive,
        promotionEnabled: hasPromotion,
        promotionPriceYuan: hasPromotion ? row.promotionPriceCents / 100 : 0,
        promotionStartAt: row.promotionStartAt ? new Date(row.promotionStartAt) : null,
        promotionEndAt: row.promotionEndAt ? new Date(row.promotionEndAt) : null,
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    resetEditForm() {
      this.$refs.form && this.$refs.form.resetFields();
      this.form = this.defaultForm();
    },
    validatePromotion() {
      if (this.form.promotionEnabled) {
        if (this.form.promotionPriceYuan === null || this.form.promotionPriceYuan === undefined) {
          this.$message.error("Promotion price is required");
          return false;
        }
        if (
          this.form.promotionStartAt &&
          this.form.promotionEndAt &&
          new Date(this.form.promotionStartAt) > new Date(this.form.promotionEndAt)
        ) {
          this.$message.error("Start time cannot be later than end time");
          return false;
        }
      }
      return true;
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        if (!this.validatePromotion()) return;
        this.saving = true;

        const payload = {
          id: this.form.id,
          productId: this.form.productId,
          durationUnit: this.form.durationUnit,
          originalPriceCents: Math.round(this.form.priceYuan * 100),
          durationValue: this.form.durationValue,
          dailyAnalysis: this.form.dailyAnalysis,
          dailyImprove: this.form.dailyImprove,
          name: this.form.name,
          targetLevel: this.form.targetLevel,
          sortOrder: this.form.sortOrder,
          isActive: this.form.isActive,
        };

        if (this.form.promotionEnabled) {
          payload.promotionPriceCents = Math.round(this.form.promotionPriceYuan * 100);
          payload.promotionStartAt = this.form.promotionStartAt ? new Date(this.form.promotionStartAt).toISOString().replace(/\.\d{3}Z$/, 'Z') : null;
          payload.promotionEndAt = this.form.promotionEndAt ? new Date(this.form.promotionEndAt).toISOString().replace(/\.\d{3}Z$/, 'Z') : null;
        } else {
          payload.promotionPriceCents = null;
          payload.promotionStartAt = null;
          payload.promotionEndAt = null;
        }

        upsertAppSubscriptionPlan(payload)
          .then((response) => {
            if (response.success) {
              this.$notify({
                title: "Update successful",
                type: "success",
              });
              this.dialogVisible = false;
              this.fetchData();
            }
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.app-subscription-plan {
  .promotion-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 5px;
    display: inline-block;

    &.success {
      background-color: #67c23a;
    }

    &.danger {
      background-color: #f56c6c;
    }
  }

  .plan-readonly-info {
    margin-bottom: 16px;
    padding: 8px 12px;
    background-color: #f5f7fa;
    border-radius: 4px;
    font-size: 13px;
    color: #606266;

    .info-item {
      margin-right: 20px;
    }
  }

  .field-hint {
    font-size: 11px;
    color: #909399;
    line-height: 1.2;
    margin-top: 2px;
  }
}
</style>
